import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useBranding } from "@/lib/branding";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Search, CheckCircle2, MapPin, HeartPulse } from "lucide-react";
import { toast } from "sonner";

export default function PublicAttend() {
  const { branding } = useBranding();
  const brand = branding?.theme_color || "#0EA5E9";
  const [empId, setEmpId] = useState("");
  const [dob, setDob] = useState("");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState(null);
  const [marked, setMarked] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      const params = new URLSearchParams({ q, page: String(page), limit: "10" });
      api.get(`/public/attend/sessions?${params}`).then((r) => {
        setResults(r.data.data || []);
        setTotal(r.data.total || 0);
      }).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [q, page]);

  async function submit() {
    if (!empId || !dob) { toast.error("HCM ID and Date of Birth are required"); return; }
    setSubmitting(true);
    try {
      const body = { emp_id: empId, dob };
      if (selected?.type === "session") body.session_id = selected.id;
      if (selected?.type === "quiz") body.questionnaire_id = selected.id;
      const r = await api.post("/public/attend", body);
      setMarked({ employee: r.data.employee, target: r.data.marked_for });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to mark attendance");
    } finally { setSubmitting(false); }
  }

  if (marked) {
    return (
      <div className="min-h-screen grid place-items-center p-6" style={{ background: `linear-gradient(135deg, ${brand}22, #fff)` }}>
        <Card className="max-w-md w-full border-slate-200 rounded-2xl">
          <CardContent className="p-8 space-y-3 text-center">
            <div className="h-14 w-14 rounded-full grid place-items-center mx-auto" style={{ background: brand + "22" }}>
              <CheckCircle2 className="h-7 w-7" style={{ color: brand }}/>
            </div>
            <h1 className="font-display font-black text-2xl">Attendance Marked</h1>
            <p className="text-slate-500">{marked.employee?.name} ({marked.employee?.emp_id})</p>
            <p className="text-sm">For: <span className="font-bold">{marked.target}</span></p>
            <Button onClick={()=>{ setMarked(null); setEmpId(""); setDob(""); setSelected(null); setQ(""); }}
              className="rounded-xl mt-4" style={{ background: brand, color: "white" }} data-testid="mark-another-btn">
              Mark for someone else
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(total / 10));

  return (
    <div className="min-h-screen p-6" style={{ background: `linear-gradient(135deg, ${brand}10, #f8fafc)` }}>
      <div className="max-w-2xl mx-auto space-y-6">
        <header className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-2xl grid place-items-center text-white shadow" style={{ background: brand }}>
            <HeartPulse className="h-6 w-6"/>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold" style={{ color: brand }}>Attendance</div>
            <h1 className="font-display font-black text-2xl text-slate-900">{branding?.hospital_name || "MedLearn"} · Mark Attendance</h1>
          </div>
        </header>

        <Card className="border-slate-200 rounded-2xl"><CardContent className="p-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-500">HCM / Employee ID *</Label>
              <Input value={empId} onChange={(e)=>setEmpId(e.target.value.trim())} placeholder="EMP001" className="h-12 text-lg" data-testid="att-empid"/>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-500">Date of Birth *</Label>
              <Input type="date" value={dob} onChange={(e)=>setDob(e.target.value)} className="h-12 text-lg" data-testid="att-dob"/>
            </div>
          </div>
        </CardContent></Card>

        <Card className="border-slate-200 rounded-2xl"><CardContent className="p-6 space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Pick Training / Session (optional)</Label>
            <div className="text-[11px] text-slate-400 mb-2">Leave blank to mark a general attendance.</div>
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 bg-white">
              <Search className="h-4 w-4 text-slate-400"/>
              <Input placeholder="Wildcard search…" value={q} onChange={(e)=>{ setQ(e.target.value); setPage(1); }}
                className="border-0 focus-visible:ring-0 shadow-none px-0 h-8" data-testid="att-search"/>
            </div>
          </div>
          <div className="space-y-1 max-h-[280px] overflow-y-auto" data-testid="att-results">
            <button type="button" onClick={()=>setSelected(null)}
              className={`w-full text-left p-3 rounded-xl border-2 transition ${selected === null ? "border-sky-500 bg-sky-50" : "border-slate-200 hover:bg-slate-50"}`}
              data-testid="att-pick-none">
              <div className="font-bold text-sm">— General Attendance —</div>
              <div className="text-[11px] text-slate-500">Just record presence, no specific session.</div>
            </button>
            {results.map((r) => (
              <button key={`${r.type}-${r.id}`} type="button" onClick={()=>setSelected(r)}
                className={`w-full text-left p-3 rounded-xl border-2 transition ${selected?.id === r.id ? "border-sky-500 bg-sky-50" : "border-slate-200 hover:bg-slate-50"}`}
                data-testid={`att-pick-${r.id}`}>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${r.type === "session" ? "bg-violet-100 text-violet-700" : "bg-emerald-100 text-emerald-700"}`}>{r.type}</span>
                  <div className="font-bold text-sm">{r.name}</div>
                </div>
                {r.description && <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-1">{r.description}</div>}
              </button>
            ))}
          </div>
          <div className="flex items-center justify-between text-xs text-slate-500">
            <div data-testid="att-page-info">Page {page} / {totalPages} · {total} items</div>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={()=>setPage(p=>p-1)} data-testid="att-prev-page">Prev</Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={()=>setPage(p=>p+1)} data-testid="att-next-page">Next</Button>
            </div>
          </div>
        </CardContent></Card>

        <Button onClick={submit} disabled={submitting || !empId || !dob} className="w-full text-white rounded-xl h-14 text-base"
          style={{ background: brand }} data-testid="att-submit">
          {submitting ? "Marking…" : selected ? `Mark for "${selected.name}"` : "Mark Attendance"}
        </Button>
      </div>
    </div>
  );
}
